import { Flex, Text } from '@chakra-ui/react'
import React, { useMemo } from 'react'
import { QueueAgentStatus } from '~/hooks/useCallQueue'
import { QueueControlButtonStyleValues } from '~/pages/Home/TopNav/componentStyles/QueueControlButton.style'
import { AgentCustomStatus } from '~/types/AgentCustomStatus'

import { MenuPlacement } from './Menu'
import { Menu } from './Menu'
import { MenuItem, MenuListItem } from './MenuItem'

type QueueStatusMenuProps = {
  selectedValue: QueueAgentStatus | number
  customStatuses: AgentCustomStatus[]
  handleSelect: (value: QueueAgentStatus | number) => string | void
  anchorRef?: React.RefObject<HTMLDivElement>
}

const QueueStatusMenu = ({
  selectedValue,
  customStatuses,
  handleSelect,
  anchorRef,
}: QueueStatusMenuProps) => {
  const menuItemData: MenuItem<QueueAgentStatus | number | null>[] =
    useMemo(() => {
      const items: MenuItem<QueueAgentStatus | number | null>[] = []

      if (selectedValue !== QueueAgentStatus.InQueue) {
        items.push({
          value: QueueAgentStatus.InQueue,
          content: <MenuListItem primaryText="Join Queue" />,
        })
      }

      if (
        selectedValue === QueueAgentStatus.InQueue ||
        typeof selectedValue === 'number'
      ) {
        items.push({
          value: QueueAgentStatus.Offline,
          content: <MenuListItem primaryText="Go Offline" />,
        })
      }

      const onlineStatuses = (customStatuses ?? []).filter(
        (status) =>
          status.availability === 'Online' && status.id !== selectedValue,
      )
      const awayStatuses = (customStatuses ?? []).filter(
        (status) =>
          status.availability === 'Away' && status.id !== selectedValue,
      )

      const showOnlineSection = onlineStatuses.length > 0
      const showAwaySection = awayStatuses.length > 0

      const onlineItems = showOnlineSection
        ? [
            {
              value: null,
              content: (
                <Flex
                  w="full"
                  padding={'0rem 0.75rem'}
                  alignItems={'center'}
                  alignSelf={'stretch'}
                  gap={'0.5rem'}
                  borderRadius={'0.25rem'}
                  background={'gray.400'}
                >
                  <Text
                    overflow={'hidden'}
                    color={'gray.1300'}
                    textOverflow={'ellipsis'}
                    fontSize={'1rem'}
                    fontWeight={'700'}
                    lineHeight={'1.5rem'}
                  >
                    Online
                  </Text>
                </Flex>
              ),
              clickDisabled: true,
            },
            ...onlineStatuses.map((status) => ({
              value: status.id,
              content: <MenuListItem primaryText={status.name} />,
            })),
          ]
        : []

      const awayItems = showAwaySection
        ? [
            {
              value: null,
              content: (
                <Flex
                  w="full"
                  padding={'0rem 0.75rem'}
                  alignItems={'center'}
                  alignSelf={'stretch'}
                  gap={'0.5rem'}
                  borderRadius={'0.25rem'}
                  background={'gray.400'}
                >
                  <Text
                    overflow={'hidden'}
                    color={'gray.1300'}
                    textOverflow={'ellipsis'}
                    fontSize={'1rem'}
                    fontWeight={'700'}
                    lineHeight={'1.5rem'}
                  >
                    Away
                  </Text>
                </Flex>
              ),
              clickDisabled: true,
            },
            ...awayStatuses.map((status) => ({
              value: status.id,
              content: <MenuListItem primaryText={status.name} />,
            })),
          ]
        : []

      return [...items, ...onlineItems, ...awayItems]
    }, [customStatuses, selectedValue])

  return (
    <Menu
      anchorRef={anchorRef}
      items={menuItemData}
      onSelect={(value) => {
        if (value) handleSelect(value)
      }}
      options={{
        width: QueueControlButtonStyleValues.menuWidth,
        placement: MenuPlacement.BottomLeft,
      }}
    />
  )
}

export default QueueStatusMenu
